import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Radio,
  Tooltip,
} from '@mui/material';
import { getApiClient } from '../../services/api/axios-config';
import SwimLaneConfirmDialog from './SwimLaneConfirmDialog';

/**
 * SwimLaneAssignmentTable - Table for assigning team members to swim lanes
 * Each swim lane (tracking status) can have one owner assigned
 * Processes assignments IMMEDIATELY when user confirms (not on modal save)
 */
const SwimLaneAssignmentTable = ({
  swimLanes = [],
  assignments = [],
  teamMembers = [],
  onAssignmentChange,
  onBoardRefreshNeeded,
  saveTeamMembersIfNeeded,
  onDurationChange,
  onTypeChange,
  onAnchorChange,
  isRecalculating = false,
  projectId,
  authToken,
  userId,
}) => {
  // Local state for assignments (updates immediately)
  const [localAssignments, setLocalAssignments] = useState(assignments);

  // State for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [previewData, setPreviewData] = useState({
    packages: [],
    count: 0,
    packagesWithAssignments: 0,
  });
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Snackbar for feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Sync local state when assignments prop changes
  React.useEffect(() => {
    setLocalAssignments(assignments);
  }, [assignments]);

  // Get the current owner for a swim lane
  const getOwnerForSwimLane = (trackingStatusId) => {
    const assignment = localAssignments.find(
      (a) => a.tracking_status_id === trackingStatusId
    );
    return assignment?.employee_id || '';
  };

  // Get employee name by ID
  const getEmployeeName = (employeeId) => {
    const member = teamMembers.find((m) => m.employee_id === employeeId);
    return member ? `${member.firstname} ${member.lastname}` : '';
  };

  // Get swim lane name by ID
  const getSwimLaneName = (trackingStatusId) => {
    const lane = swimLanes.find((l) => l.id === trackingStatusId);
    return lane?.label || lane?.name || '';
  };

  // Handle owner selection change
  const handleOwnerChange = async (trackingStatusId, newEmployeeId) => {
    const currentEmployeeId = getOwnerForSwimLane(trackingStatusId);

    // If clearing assignment (removing owner), call unassign API immediately
    if (!newEmployeeId && currentEmployeeId) {
      await handleRemoveAssignment(trackingStatusId, currentEmployeeId);
      return;
    }

    // If no change, do nothing
    if (newEmployeeId === currentEmployeeId) {
      return;
    }

    // If changing to a new owner, first remove old then add new
    // Fetch preview data for confirmation dialog
    setLoadingPreview(true);
    setConfirmDialogOpen(true);
    setPendingAssignment({
      trackingStatusId,
      newEmployeeId,
      currentEmployeeId,
    });

    try {
      const apiClient = getApiClient(authToken);
      const response = await apiClient.get(
        `/api/projects/${projectId}/swimlane-assignments/preview`,
        {
          params: {
            trackingStatusId,
            employeeId: newEmployeeId,
          },
        }
      );

      const data = response.data.data || {
        packages: [],
        packageCount: 0,
        packagesWithAssignments: 0,
      };

      // If there are NO packages, skip dialog and assign immediately
      if (data.packageCount === 0) {
        setConfirmDialogOpen(false);
        setPendingAssignment(null);
        await handleAssignWithoutConfirmation(
          trackingStatusId,
          newEmployeeId,
          currentEmployeeId
        );
        return;
      }

      setPreviewData({
        packages: data.packages || [],
        count: data.packageCount || 0,
        packagesWithAssignments: data.packagesWithAssignments || 0,
      });
    } catch (err) {
      console.error('Error fetching swim lane preview:', err);
      // Still allow assignment even if preview fails
      setPreviewData({ packages: [], count: 0, packagesWithAssignments: 0 });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle assignment when no confirmation is needed (0 packages)
  const handleAssignWithoutConfirmation = async (
    trackingStatusId,
    newEmployeeId,
    currentEmployeeId
  ) => {
    try {
      const apiClient = getApiClient(authToken);

      // If there's an existing owner, remove them first
      if (currentEmployeeId) {
        await apiClient.delete(
          `/api/projects/${projectId}/swimlane-assignments/unassign`,
          {
            params: {
              employeeId: currentEmployeeId,
              trackingStatusId,
            },
          }
        );
      }

      // Create new assignment
      const response = await apiClient.post(
        `/api/projects/${projectId}/swimlane-assignments/assign`,
        {
          employeeId: newEmployeeId,
          trackingStatusId,
          autoAssignPackages: false, // No packages to assign
          userId,
        }
      );

      // Update local state
      setLocalAssignments((prev) => {
        const filtered = prev.filter(
          (a) => a.tracking_status_id !== trackingStatusId
        );
        return [
          ...filtered,
          {
            tracking_status_id: trackingStatusId,
            employee_id: newEmployeeId,
            id: response.data.data?.assignmentId,
          },
        ];
      });

      // Notify parent
      onAssignmentChange?.();

      setSnackbar({
        open: true,
        message: `Swim lane assigned to ${getEmployeeName(newEmployeeId)}`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Error creating assignment:', err);
      setSnackbar({
        open: true,
        message: 'Failed to assign swim lane',
        severity: 'error',
      });
    }
  };

  // Handle confirmation dialog confirm with auto-assign option
  const handleConfirm = async (autoAssign) => {
    if (!pendingAssignment) return;

    const { trackingStatusId, newEmployeeId, currentEmployeeId } =
      pendingAssignment;

    try {
      // First, save any pending team member changes to ensure resource exists in DB
      if (saveTeamMembersIfNeeded) {
        const saved = await saveTeamMembersIfNeeded();
        if (!saved) {
          setSnackbar({
            open: true,
            message: 'Failed to save team members. Please try again.',
            severity: 'error',
          });
          return;
        }
      }

      const apiClient = getApiClient(authToken);

      // If there's an existing owner, remove them first
      if (currentEmployeeId) {
        await apiClient.delete(
          `/api/projects/${projectId}/swimlane-assignments/unassign`,
          {
            params: {
              employeeId: currentEmployeeId,
              trackingStatusId,
            },
          }
        );
      }

      // Create new assignment with auto-assign option
      const response = await apiClient.post(
        `/api/projects/${projectId}/swimlane-assignments/assign`,
        {
          employeeId: newEmployeeId,
          trackingStatusId,
          autoAssignPackages: autoAssign,
          userId,
        }
      );

      const result = response.data.data || {};

      // Update local state
      setLocalAssignments((prev) => {
        const filtered = prev.filter(
          (a) => a.tracking_status_id !== trackingStatusId
        );
        return [
          ...filtered,
          {
            tracking_status_id: trackingStatusId,
            employee_id: newEmployeeId,
            id: result.assignmentId,
          },
        ];
      });

      // Notify parent
      onAssignmentChange?.();

      // Show success notification
      if (autoAssign && result.packagesAutoAssigned > 0) {
        setSnackbar({
          open: true,
          message: `${result.packagesAutoAssigned} packages assigned to ${getEmployeeName(newEmployeeId)}${result.assignmentsCleared > 0 ? `, ${result.assignmentsCleared} existing assignments cleared` : ''}`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Swim lane assigned to ${getEmployeeName(newEmployeeId)}`,
          severity: 'success',
        });
      }

      // Always refresh the board to show updated assignments
      onBoardRefreshNeeded?.();
    } catch (err) {
      console.error('Error creating assignment:', err);
      setSnackbar({
        open: true,
        message: 'Failed to assign swim lane',
        severity: 'error',
      });
    } finally {
      setConfirmDialogOpen(false);
      setPendingAssignment(null);
      setPreviewData({ packages: [], count: 0, packagesWithAssignments: 0 });
    }
  };

  // Handle removing an assignment immediately
  const handleRemoveAssignment = async (trackingStatusId, employeeId) => {
    try {
      const apiClient = getApiClient(authToken);

      await apiClient.delete(
        `/api/projects/${projectId}/swimlane-assignments/unassign`,
        {
          params: {
            employeeId,
            trackingStatusId,
          },
        }
      );

      // Update local state
      setLocalAssignments((prev) =>
        prev.filter((a) => a.tracking_status_id !== trackingStatusId)
      );

      // Notify parent
      onAssignmentChange?.();

      setSnackbar({
        open: true,
        message: `Swim lane removed from ${getEmployeeName(employeeId)}`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Error removing assignment:', err);
      setSnackbar({
        open: true,
        message: 'Failed to remove swim lane',
        severity: 'error',
      });
    }
  };

  // Handle confirmation dialog cancel
  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setPendingAssignment(null);
    setPreviewData({ packages: [], count: 0, packagesWithAssignments: 0 });
  };

  if (swimLanes.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No swim lanes found for this workflow.
        </Typography>
      </Box>
    );
  }

  if (teamMembers.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Add team members above before assigning swim lane owners.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>
                Sequence
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Swim Lane</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '250px' }}>
                Default Owner
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '140px' }}>
                Default Duration
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '160px' }}>
                Type
              </TableCell>
              <TableCell
                sx={{ fontWeight: 'bold', width: '90px' }}
                align="center"
              >
                Anchor
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {swimLanes.map((lane, index) => (
              <TableRow key={lane.id} hover>
                <TableCell>
                  <Chip
                    label={lane.sequencenumber || index + 1}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {lane.color && (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: lane.color,
                          border: '1px solid rgba(0,0,0,0.2)',
                        }}
                      />
                    )}
                    <Typography variant="body2">
                      {lane.label || lane.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={getOwnerForSwimLane(lane.id)}
                      onChange={(e) =>
                        handleOwnerChange(lane.id, e.target.value)
                      }
                      displayEmpty
                      disabled={isRecalculating}
                    >
                      <MenuItem value="">
                        <em>No owner assigned</em>
                      </MenuItem>
                      {teamMembers.map((member) => (
                        <MenuItem
                          key={member.employee_id}
                          value={member.employee_id}
                        >
                          {member.firstname} {member.lastname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={
                      lane.default_duration_days != null
                        ? lane.default_duration_days
                        : ''
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      onDurationChange?.(
                        lane.id,
                        val === '' ? null : parseInt(val, 10)
                      );
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">days</InputAdornment>
                      ),
                      inputProps: { min: 1 },
                    }}
                    size="small"
                    disabled={isRecalculating}
                    sx={{ width: '120px' }}
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={lane.swim_lane_type || 'standard'}
                      onChange={(e) => onTypeChange?.(lane.id, e.target.value)}
                      disabled={isRecalculating}
                    >
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="vdc">VDC</MenuItem>
                      <MenuItem value="prefab">Prefabrication</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Radio
                      checked={!!lane.is_field_installation_anchor}
                      onChange={() => onAnchorChange?.(lane.id)}
                      disabled={isRecalculating}
                      size="small"
                    />
                    {lane.is_field_installation_anchor && (
                      <Tooltip title="Field Installation Anchor — Reference point for all due date calculations">
                        <span style={{ fontSize: '1.2rem' }}>⚓</span>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog */}
      <SwimLaneConfirmDialog
        open={confirmDialogOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        employeeName={
          pendingAssignment
            ? getEmployeeName(pendingAssignment.newEmployeeId)
            : ''
        }
        swimLaneName={
          pendingAssignment
            ? getSwimLaneName(pendingAssignment.trackingStatusId)
            : ''
        }
        packageCount={previewData.count}
        packagesWithAssignments={previewData.packagesWithAssignments}
        packages={previewData.packages}
        loading={loadingPreview}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SwimLaneAssignmentTable;
